import { mdiClose } from "@mdi/js";
import Icon from "@mdi/react";
import { Button, Dialog, FormControlLabel, Grid, Switch, TextField, Tooltip, Typography } from "@mui/material";
import { DatePicker, DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import React, { useEffect, useState } from "react";
import { useSnackbar } from "../../components/context/SnackbarContext";
import useAxios from "../../hooks/useAxios";
import { useLoader } from "../../components/context/LoaderContext";
import { Formik } from "formik";
import * as yup from "yup";
import moment from "moment-timezone";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { useSelector } from "react-redux";
import { displayDate, PickerWrapper } from "../../utils/Utils";
import DynamicFields from "./CustomFields";


function AddEventDialog({ onClose, open, type, dataId, handleReloadFunc, editId }) {
    const axiosInstance = useAxios();
    const { showSnackbar } = useSnackbar();
    const { showLoader, hideLoader } = useLoader();
    const [eventList, setEventList] = useState([])
    const [isAllDay, setIsAllDay] = useState(false)
    const { preferredDateFormat, preTimeZoneKey } = useSelector((state) => state?.user);
    const [customFields, setCustomFields] = useState([]);

    useEffect(() => {
        if (editId) {
            const fetchData = async () => {
                await getEvent();
            };
            fetchData();
        } else {
            async function fetchData() {
                getData();
            }
            fetchData();
        }
    }, []);


    const getData = async () => {
        showLoader();
        await axiosInstance.get(`api/${type}-event/create/?${type}Id=${dataId}`).then((response) => {
            if (response.status === 200) {
                setCustomFields(response?.data?.data?.customFields || []);
                hideLoader();
            }
        })
            .then((json) => { })
            .catch((error) => {
                if (error.response) {
                    hideLoader();
                    if (error.response.status === 400) {
                        showSnackbar(error.response.data.message, "warning");
                    }
                }
            });
    }

    //=======Start::Write a code for manage Edit Notes======//

    async function getEvent() {
        showLoader()
        try {
            const response = await axiosInstance.get(`api/${type}-event/edit/${editId}?${type}Id=${dataId}`);
            if (response.data.code === 200) {
                setEventList(response?.data?.data?.record);
                setIsAllDay(response?.data?.data?.record?.is_fullday ? true : false)
                setCustomFields(response?.data?.data?.customFields || []);
            }
        } catch (error) {
            if (error.response) {
                if (error.response.status === 400) {
                    showSnackbar(error.response.data.message, "warning");
                }
            }
        } finally {
            hideLoader();
        }
    }
    //=======Start::Write a code for manage Edit Notes======//

    const buildInitialValues = (customFields) => {
        // Default initial values for standard fields
        const initial = {
            name: eventList?.name || '',
            description: eventList?.description || '',
            start_date: eventList?.start_date ? moment(eventList?.start_date) : moment(),
            end_date: eventList?.end_date ? moment(eventList?.end_date) : moment().add(1, 'days'),
            location: eventList?.location || '',
            error_list: [],
        };

        const customValuesMap = {};

        if (Array.isArray(eventList?.custom_field_values)) {
            eventList.custom_field_values.forEach(item => {
                if (item?.field_name) {
                    customValuesMap[item.field_name.trim()] = item.value;
                }
            });
        }

        customFields.forEach(field => {
            const key = field.field_name?.trim();
            const fieldValue = customValuesMap[key];

            switch (field.display_type) {
                case 'checkbox':
                    try {
                        // If it's a stored JSON string like '["a", "b"]'
                        initial[key] = fieldValue ? JSON.parse(fieldValue) : [];
                    } catch {
                        initial[key] = Array.isArray(fieldValue) ? fieldValue : [];
                    }
                    break;

                case 'file':
                    // You can customize this if you support showing uploaded file names
                    initial[key] = []; // Typically empty unless you want to preview old uploads
                    break;

                case 'date':
                case 'datetime':
                    initial[key] = fieldValue ? moment(fieldValue) : '';
                    break;

                default:
                    initial[key] = fieldValue ?? '';
                    break;
            }
        });

        return initial;
    };


    const buildValidationSchema = (customFields) => {
        const schema = {
            name: yup
                .string()
                .required("Enter event subject")
                .min(2, "The name must be at least 2 characters.")
                .max(50, "The name must not be greater than 50 characters."),
            description: yup
                .string()
                .required("Enter event description")
                .min(2, "The description must be at least 2 characters.")
                .max(150, "The description must not be greater than 150 characters."),
            start_date: yup
                .string()
                .required("Enter start date"),
            end_date: yup
                .string()
                .required("Enter end date"),
        };

        customFields.forEach(field => {
            const key = field?.field_name;
            const label = field?.label || field?.field_name;
            const isRequired = field?.is_required === 'yes';
            const minLength = field?.min_input ? parseInt(field.min_input) : undefined;
            const maxLength = field?.max_input ? parseInt(field.max_input) : undefined;

            switch (field?.display_type) {
                case 'text':
                case 'textarea':
                    schema[key] = yup.string();
                    if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                    if (minLength) schema[key] = schema[key].min(minLength, `${label} must be at least ${minLength} characters`);
                    if (maxLength) schema[key] = schema[key].max(maxLength, `${label} must be at most ${maxLength} characters`);
                    break;

                case 'email':
                    schema[key] = yup.string().email('Enter a valid email');
                    if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                    if (minLength) schema[key] = schema[key].min(minLength, `${label} must be at least ${minLength} characters`);
                    if (maxLength) schema[key] = schema[key].max(maxLength, `${label} must be at most ${maxLength} characters`);
                    break;

                case 'phone':
                    schema[key] = yup
                        .string()
                        .matches(/^[0-9()+-\s]*$/, 'Enter a valid phone number');
                    if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                    if (minLength) schema[key] = schema[key].min(minLength, `${label} must be at least ${minLength} characters`);
                    if (maxLength) schema[key] = schema[key].max(maxLength, `${label} must be at most ${maxLength} characters`);
                    break;

                case 'number':
                    schema[key] = yup
                        .number()
                        .typeError(`${label} must be a number`);
                    if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                    if (minLength) schema[key] = schema[key].min(minLength, `${label} must be at least ${minLength} characters`);
                    if (maxLength) schema[key] = schema[key].max(maxLength, `${label} must be at most ${maxLength} characters`);
                    break;

                case 'select':
                    if (field?.is_multiple === 'yes') {
                        // MULTI-SELECT
                        schema[key] = yup.array().of(yup.string());
                        if (isRequired) {
                            schema[key] = schema[key].min(1, `Select at least one ${label}`);
                        }
                    } else {
                        // SINGLE SELECT
                        schema[key] = yup.string();
                        if (isRequired) {
                            schema[key] = schema[key].required(`${label} is required`);
                        }
                    }
                    break;
                case 'radio':
                    schema[key] = yup.string();
                    if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                    break;

                case 'checkbox':
                    schema[key] = yup.array();
                    if (isRequired) schema[key] = schema[key].min(1, `Select at least one ${label}`);
                    break;

                case 'date':
                case 'datetime':
                    schema[key] = yup.string();
                    if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                    break;

                case 'file':
                    schema[key] = yup.array().of(
                        yup
                            .mixed()
                            .test('fileSize', 'File too large', value => !value || value.size <= 4 * 1024 * 1024)
                    );
                    if (isRequired) {
                        schema[key] = schema[key].min(1, `Upload at least one ${label}`);
                    }
                    break;

                default:
                    schema[key] = yup.string();
                    if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                    break;
            }
        });

        return yup.object().shape(schema);
    };

    const staticFields = ['id', 'leadId', 'dealId', 'name', 'description', 'allDay', 'start_date', 'end_date', 'location'];


    const handleAllDay = () => {
        setIsAllDay(prevIsAllDay => !prevIsAllDay);
    };

    const handleSubmit = async (values, onSubmitProps) => {
        const data = {
            id: eventList?.id,
            leadId: dataId,
            dealId: dataId,
            name: values?.name,
            description: values?.description,
            allDay: isAllDay ? "yes" : "no",
            start_date: values?.start_date ? isAllDay ? moment(values.start_date).format("YYYY-MM-DD") : moment(values.start_date).format("YYYY-MM-DD HH:mm:ss") : null,
            end_date: values.end_date ? isAllDay ? moment(values.end_date).format("YYYY-MM-DD") : moment(values.end_date).format("YYYY-MM-DD HH:mm:ss") : null,
            location: values.location,
        };

        const formData = new FormData();
        const customFieldsData = {};

        Object.entries(data).forEach(([key, value]) => {
            formData.append(key, value ?? '');
        });

        Object.entries(values).forEach(([key, value]) => {
            if (!staticFields.includes(key)) {
                const isFileArray = Array.isArray(value) && value.length && value[0] instanceof File;

                if (isFileArray) {
                    value.forEach((file, idx) => {
                        //formData.append(`${key}[${idx}]`, file);
                        formData.append(`${key}[]`, file);
                    });
                } else {
                    customFieldsData[key] = value;
                }
            }
        });

        // Append custom fields JSON string under one key
        formData.append('custom_fields', JSON.stringify(customFieldsData));

        showLoader();
        await axiosInstance.post(`/api/${type}-event/${editId ? 'update' : 'store'}`, formData).then(response => {
            if (response.status === 200) {
                hideLoader()
                showSnackbar(response.data.message, 'success')
                onClose()
                handleReloadFunc()
            }
        }).then(json => {

        }).catch(error => {
            if (error.response) {
                hideLoader()
                if (error.response.status === 422 || error.response.data.validation_errors) {
                    const errors = error.response.data.validation_errors;
                    Object.keys(errors).forEach(key => {
                        onSubmitProps.setFieldError(key, errors[key][0]);
                    });

                } else {
                    showSnackbar(error.response.data.message, 'error')
                }
            }
        });
    }

    return (
        <>
            <Dialog onClose={onClose} open={open}>
                <div className="dialog-title">
                    <h2>{editId ? 'Edit' : 'Add'} Event</h2>
                    <Tooltip title="Close">
                        <button className="icon-button dialog-close" onClick={onClose}>
                            <Icon path={mdiClose} color={'currentColor'} size={1} />
                        </button>
                    </Tooltip>
                </div>
                <Formik
                    initialValues={buildInitialValues(customFields)}
                    validationSchema={buildValidationSchema(customFields)}
                    onSubmit={handleSubmit}
                    //enableReinitialize={editId ? true : false}
                    enableReinitialize
                >
                    {props => {
                        const {
                            touched,
                            errors,
                            values,
                            handleChange,
                            handleBlur,
                            handleSubmit,
                            setFieldValue,
                            setFieldTouched
                        } = props;
                        return (
                            <form onSubmit={handleSubmit}>
                                <TextField
                                    label="Subject"
                                    variant="filled"
                                    name="name"
                                    value={values?.name || ''}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.name && touched.name}
                                    helperText={(errors.name && touched.name) && errors.name}
                                />

                                <TextField
                                    label="Description"
                                    variant="filled"
                                    name="description"
                                    value={values?.description || ''}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    multiline
                                    rows={3}
                                    error={errors.description && touched.description}
                                    helperText={(errors.description && touched.description) && errors.description}
                                />

                                <LocalizationProvider dateAdapter={AdapterMoment}>
                                    {
                                        isAllDay ?
                                            <DatePicker
                                                label="Start Date"
                                                format={displayDate(preferredDateFormat, 'half')}
                                                views={['year', 'month', 'day']}
                                                slotProps={{
                                                    textField: {
                                                        variant: 'filled',
                                                        name: "start_date",
                                                        type: 'text',
                                                        error: errors.start_date && touched.start_date,
                                                        helperText: (errors.start_date && touched.start_date) && errors.start_date
                                                    }
                                                }}
                                                name="start_date"
                                                value={values?.start_date}
                                                onChange={(value) => setFieldValue("start_date", value, true)}
                                                onBlur={handleBlur}
                                            />
                                            :
                                            <DateTimePicker
                                                label="Start Date"
                                                format={preferredDateFormat}
                                                views={["day", "hours", "minutes", "month", "year"]}
                                                name="start_date"
                                                slots={{
                                                    popper: PickerWrapper
                                                }}
                                                slotProps={{
                                                    textField: {
                                                        variant: 'filled',
                                                        name: "start_date",
                                                        type: 'text',
                                                        error: errors.start_date && touched.start_date,
                                                        helperText: (errors.start_date && touched.start_date) && errors.start_date
                                                    }
                                                }}
                                                value={values?.start_date ? moment.utc(values.start_date).tz(preTimeZoneKey) : moment()}
                                                onChange={(value) => setFieldValue("start_date", value, true)}
                                                onBlur={handleBlur}
                                            />
                                    }

                                </LocalizationProvider>

                                <LocalizationProvider dateAdapter={AdapterMoment}>
                                    {
                                        isAllDay ?
                                            <DatePicker
                                                label="End Date"
                                                format={displayDate(preferredDateFormat, 'half')}
                                                views={['year', 'month', 'day']}
                                                name="end_date"
                                                slotProps={{
                                                    textField: {
                                                        variant: 'filled',
                                                        name: "end_date",
                                                        type: 'text',
                                                        error: errors.end_date && touched.end_date,
                                                        helperText: (errors.end_date && touched.end_date) && errors.end_date
                                                    }
                                                }}
                                                value={values?.end_date ? moment.utc(values.end_date).tz(preTimeZoneKey) : moment()}
                                                onChange={(value) => setFieldValue("end_date", value, true)}
                                                onBlur={handleBlur}

                                            /> :
                                            <DateTimePicker
                                                label="End Date"
                                                format={preferredDateFormat}
                                                views={["day", "hours", "minutes", "month", "year"]}
                                                name="end_date"
                                                slots={{
                                                    popper: PickerWrapper
                                                }}
                                                slotProps={{
                                                    textField: {
                                                        variant: 'filled',
                                                        name: "end_date",
                                                        type: 'text',
                                                        error: errors.end_date && touched.end_date,
                                                        helperText: (errors.end_date && touched.end_date) && errors.end_date
                                                    }
                                                }}
                                                value={values?.end_date ? moment.utc(values.end_date).tz(preTimeZoneKey) : moment()}
                                                onChange={(value) => setFieldValue("end_date", value, true)}
                                                onBlur={handleBlur}
                                            />
                                    }

                                </LocalizationProvider>

                                <div className="form-group">
                                    <FormControlLabel control={<Switch onChange={() => handleAllDay(isAllDay)} checked={isAllDay} />} label="All Day Event" />
                                </div>
                                <TextField
                                    label="Location"
                                    variant="filled"
                                    name="location"
                                    value={values?.location || ''}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.location && touched.location}
                                    helperText={(errors.location && touched.location) && errors.location}
                                />
                                {customFields?.length > 0 &&
                                    <Typography variant="title-medium-large-semibold" component={'h4'} className="mb-12">Custom Fields</Typography>
                                }
                                {customFields?.length > 0 &&
                                    customFields.map((field, idx) => (
                                        <DynamicFields
                                            key={idx}
                                            field={field}
                                            values={values}
                                            handleChange={handleChange}
                                            handleBlur={handleBlur}
                                            errors={errors}
                                            touched={touched}
                                            setFieldValue={setFieldValue}
                                            selectedFiles={editId ? eventList?.custom_field_values : []}
                                            setFieldTouched={setFieldTouched}
                                        />
                                    ))
                                }

                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Button className="btn-block" type="submit">{editId ? 'Edit' : 'Add'} Event</Button>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Button color="secondary" className="btn-block" onClick={onClose}>Cancel</Button>
                                    </Grid>
                                </Grid>
                            </form>
                        );
                    }}
                </Formik>
            </Dialog >
        </>
    );
}

export default AddEventDialog;